import React, { Fragment, useState, useEffect, useCallback } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Row,
 Col,
 Card,
 Table,
 Badge,
 Button,
 Form,
 Modal,
 Tab,
 Nav,
 Spinner,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PermissionWrapper from "../PermissionWrapper";
import api from "../../../api";
import {
 getQCInspections,
 deleteQCInspection,
 approveQCInspection,
 rejectQCInspection,
 getQCStats,
} from "../../../services/qcService";
import QCViewModal from "./QCViewModal";
import NCRMonitor from "./NCRMonitor";

/* Constants */
const STATUS_COLORS = {
 Draft: "secondary",
 Submitted: "warning",
 Approved: "success",
 Rejected: "danger",
 Closed: "dark",
};

const RESULT_COLORS = {
 Pass: "success",
 Fail: "danger",
 "Conditional Pass": "warning",
 Pending: "secondary",
};

const INSPECTION_TYPES = [
 "Field Inspection",
 "Lab Test",
 "Material Inspection",
 "Process Inspection",
 "Final Inspection",
 "In-Process Inspection",
 "Third Party Inspection",
 "Other",
];

/* Component */
const QCList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails || {});

 const [inspections, setInspections] = useState([]);
 const [loading, setLoading] = useState(true);
 const [stats, setStats] = useState(null);
 const [workOrders, setWorkOrders] = useState([]);
 const [projects, setProjects] = useState([]);

 const [selectedRecord, setSelectedRecord] = useState(null);
 const [showViewModal, setShowViewModal] = useState(false);

 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });

 const [filters, setFilters] = usePersistentFilters("qc_list_filters", {
 search: "",
 status: "",
 inspectionType: "",
 workOrder: "",
 fromDate: "",
 toDate: "",
 });

 /* Load reference data */
 useEffect(() => {
 const loadRefs = async () => {
 try {
 const [woRes, projRes] = await Promise.all([
 api.get("/work-orders?limit=500"),
 api.get("/projects?limit=500"),
 ]);
 setWorkOrders(woRes.data?.data || []);
 setProjects(projRes.data?.data || []);
 } catch (_) {}
 };
 loadRefs();
 }, []);

 /* Fetch inspections */
 const fetchInspections = useCallback(async () => {
 setLoading(true);
 try {
 const params = {
 page: pagination.currentPage,
 limit: pagination.limit,
 ...(filters.search && { search: filters.search }),
 ...(filters.status && { status: filters.status }),
 ...(filters.inspectionType && { inspectionType: filters.inspectionType }),
 ...(filters.workOrder && { workOrder: filters.workOrder }),
 ...(filters.fromDate && { fromDate: filters.fromDate }),
 ...(filters.toDate && { toDate: filters.toDate }),
 };
 const res = await getQCInspections(params);
 if (res.data.success) {
 setInspections(res.data.data);
 setPagination((prev) => ({
 ...prev,
 totalPages: res.data.pagination.totalPages,
 totalCount: res.data.pagination.totalCount,
 currentPage: res.data.pagination.currentPage,
 }));
 }
 } catch (_) {
 toast.error("Failed to load QC records");
 } finally {
 setLoading(false);
 }
 }, [pagination.currentPage, pagination.limit, filters]);

 useEffect(() => {
 fetchInspections();
 }, [fetchInspections]);

 /* Fetch stats */
 const fetchStats = useCallback(async () => {
 try {
 const res = await getQCStats();
 if (res.data.success) setStats(res.data.data);
 } catch (_) {}
 }, []);

 useEffect(() => {
 fetchStats();
 }, [fetchStats]);

 /* Handlers */
 const handleFilterChange = (name, value) => {
 setFilters((prev) => ({ ...prev, [name]: value }));
 setPagination((prev) => ({ ...prev, currentPage: 1 }));
 };

 const handleView = async (record) => {
 try {
 const { getQCInspection } = await import("../../../services/qcService");
 const res = await getQCInspection(record._id);
 if (res.data.success) {
 setSelectedRecord(res.data.data);
 setShowViewModal(true);
 }
 } catch (_) {
 toast.error("Failed to load record details");
 }
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Delete QC Inspection?",
 text: "This record will be permanently removed.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 await deleteQCInspection(id);
 toast.success("Record deleted");
 fetchInspections();
 fetchStats();
 } catch (_) {
 toast.error("Failed to delete");
 }
 };

 const handleApprove = async (id) => {
 const remarks = window.prompt("Approval remarks (optional):");
 if (remarks === null) return; // cancelled
 try {
 await approveQCInspection(id, { approvalRemarks: remarks || "" });
 toast.success("QC Inspection approved");
 fetchInspections();
 fetchStats();
 } catch (_) {
 toast.error("Failed to approve");
 }
 };

 const handleReject = async (id) => {
 const remarks = window.prompt("Rejection reason (required):");
 if (!remarks) return;
 try {
 await rejectQCInspection(id, { approvalRemarks: remarks });
 toast.success("QC Inspection rejected");
 fetchInspections();
 fetchStats();
 } catch (_) {
 toast.error("Failed to reject");
 }
 };

 /* Stats Cards */
 const statsCards = stats
 ? [
 { label: "Total", value: stats.total, color: "primary", icon: "fa-clipboard-list" },
 { label: "Pass", value: stats.pass, color: "success", icon: "fa-check-circle" },
 { label: "Fail", value: stats.fail, color: "danger", icon: "fa-times-circle" },
 { label: "Pending", value: stats.pending, color: "warning", icon: "fa-hourglass-half" },
 { label: "NCR Raised", value: stats.ncr, color: "dark", icon: "fa-exclamation-triangle" },
 ]
 : [];

 return (
 <Fragment>
 <PageTitle activeMenu="QC Inspections" motherMenu="QC" />

 {/* Stats */}
 {stats && (
 <Row className="mb-3">
 {statsCards.map((s) => (
 <Col key={s.label} xs={6} sm={4} md={2} lg className="mb-3">
 <Card className={`bg-${s.color} text-white shadow-sm`}>
 <Card.Body className="p-3 d-flex justify-content-between align-items-center">
 <div>
 <p className="mb-0" style={{ fontSize: "0.73rem", opacity: 0.85 }}>
 {s.label}
 </p>
 <h4 className="mb-0 fw-bold">{s.value}</h4>
 </div>
 <i className={`fas ${s.icon} fa-2x opacity-50`} />
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>
 )}

 {/* Tabs */}
 <Tab.Container defaultActiveKey="inspections">
 <Card className="shadow-sm">
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-0">
 <Nav variant="tabs" className="border-bottom-0">
 <Nav.Item>
 <Nav.Link eventKey="inspections" className="fw-semibold">
 <i className="fas fa-microscope me-1" /> Inspections
 </Nav.Link>
 </Nav.Item>
 <Nav.Item>
 <Nav.Link eventKey="ncr" className="fw-semibold">
 <i className="fas fa-exclamation-triangle me-1 text-danger" /> NCR Monitor
 {stats?.ncr > 0 && (
 <Badge bg="danger" className="ms-1" style={{ fontSize: "0.65rem" }}>
 {stats.ncr}
 </Badge>
 )}
 </Nav.Link>
 </Nav.Item>
 </Nav>
 <div className="d-flex gap-2 flex-wrap align-items-center pb-2">
 <Button
 variant="outline-primary"
 size="sm"
 onClick={() => navigate("../qc/report")}
 >
 <i className="fas fa-file-alt me-1" /> QC Report
 </Button>
 <PermissionWrapper
 user={user}
 required={["create:qc"]}
 mode="some"
 >
 <Link className="btn btn-primary btn-sm" to="/qc/add">
 <i className="fas fa-plus me-1" /> New Inspection
 </Link>
 </PermissionWrapper>
 </div>
 </Card.Header>

 <Tab.Content>
 {/* TAB 1: Inspections */}
 <Tab.Pane eventKey="inspections">
 {/* Filters */}
 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: 180 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search</label>
 <Form.Control
 size="sm"
 placeholder="QC No / scope / location..."
 value={filters.search}
 onChange={(e) => handleFilterChange("search", e.target.value)}
 />
 </div>

 <div className="d-flex flex-column" style={{ width: 140 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <Form.Select
 size="sm"
 value={filters.status}
 onChange={(e) => handleFilterChange("status", e.target.value)}
 >
 <option value="">All</option>
 {["Draft", "Submitted", "Approved", "Rejected", "Closed"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </div>

 <div className="d-flex flex-column" style={{ width: 190 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Type</label>
 <Form.Select
 size="sm"
 value={filters.inspectionType}
 onChange={(e) => handleFilterChange("inspectionType", e.target.value)}
 >
 <option value="">All Types</option>
 {INSPECTION_TYPES.map((t) => (
 <option key={t} value={t}>{t}</option>
 ))}
 </Form.Select>
 </div>

 <div className="d-flex flex-column" style={{ width: 190 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Work Order</label>
 <Form.Select
 size="sm"
 value={filters.workOrder}
 onChange={(e) => handleFilterChange("workOrder", e.target.value)}
 >
 <option value="">All Work Orders</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </div>

 <div className="d-flex flex-column" style={{ width: 130 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From</label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.fromDate}
 onChange={(e) => handleFilterChange("fromDate", e.target.value)}
 />
 </div>

 <div className="d-flex flex-column" style={{ width: 130 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To</label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.toDate}
 onChange={(e) => handleFilterChange("toDate", e.target.value)}
 />
 </div>

 <div className="ms-auto d-flex flex-column" style={{ width: 65 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <Form.Select
 size="sm"
 value={pagination.limit}
 onChange={(e) =>
 setPagination((prev) => ({
 ...prev,
 currentPage: 1,
 limit: Number(e.target.value),
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </Form.Select>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 ) : inspections.length === 0 ? (
 <div className="text-center py-5">
 <i className="fas fa-microscope fa-3x text-muted mb-3 d-block" />
 <h5 className="text-muted">No QC records found</h5>
 <p className="text-muted">Create the first inspection using the button above.</p>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>QC No</th>
 <th>Date</th>
 <th>Type</th>
 <th>Activity / Scope</th>
 <th>Work Order</th>
 <th>Observations</th>
 <th>Result</th>
 <th>NCR</th>
 <th>Status</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {inspections.map((rec, i) => (
 <tr key={rec._id}>
 <td>
 {(pagination.currentPage - 1) * pagination.limit + i + 1}
 </td>
 <td>
 <strong className="text-primary">{rec.qcNumber}</strong>
 </td>
 <td>{new Date(rec.inspectionDate).toLocaleDateString("en-IN")}</td>
 <td>
 <Badge bg="light" text="dark" className="border">
 {rec.inspectionType}
 </Badge>
 </td>
 <td>
 <div className="fw-semibold">{rec.activityOrScope}</div>
 {rec.location && (
 <small className="text-muted">
 <i className="fas fa-map-marker-alt me-1" />
 {rec.location}
 </small>
 )}
 </td>
 <td>
 <span className="small text-primary">
 {rec.workOrder?.woNo || "—"}
 </span>
 </td>
 <td className="text-center">
 <Badge bg="light" text="dark" className="border">
 {rec.observations?.length || 0}
 </Badge>
 </td>
 <td>
 <Badge bg={RESULT_COLORS[rec.overallResult] || "secondary"}>
 {rec.overallResult}
 </Badge>
 </td>
 <td>
 {rec.nonConformanceRaised ? (
 <Badge bg={rec.ncrClosedDate ? "success" : "danger"}>
 {rec.ncrClosedDate ? "NCR Closed" : "NCR Open"}
 </Badge>
 ) : (
 <span className="text-muted">—</span>
 )}
 </td>
 <td>
 <Badge bg={STATUS_COLORS[rec.status] || "secondary"}>
 {rec.status}
 </Badge>
 </td>
 <td>
 <div className="d-flex align-items-center gap-1 flex-nowrap">
 <PermissionWrapper user={user} required={["read:qc"]}>
 <Button
 size="sm"
 variant="outline-primary"
 title="View"
 onClick={() => handleView(rec)}
 >
 <i className="fas fa-eye" />
 </Button>
 </PermissionWrapper>

 <PermissionWrapper
 user={user}
 required={["edit:qc"]}
 mode="some"
 >
 <Button
 size="sm"
 variant="outline-secondary"
 title="Edit"
 onClick={() => navigate(`/qc/edit/${rec._id}`)}
 >
 <i className="fas fa-edit" />
 </Button>
 </PermissionWrapper>

 {(rec.status === "Draft" || rec.status === "Submitted") && (
 <PermissionWrapper
 user={user}
 required={["approve:qc"]}
 mode="some"
 >
 <Button
 size="sm"
 variant="success"
 title="Approve"
 onClick={() => handleApprove(rec._id)}
 >
 Approve
 </Button>
 <Button
 size="sm"
 variant="danger"
 title="Reject"
 onClick={() => handleReject(rec._id)}
 >
 Reject
 </Button>
 </PermissionWrapper>
 )}

 <PermissionWrapper
 user={user}
 required={["delete:qc"]}
 mode="some"
 >
 <Button
 size="sm"
 variant="outline-danger"
 title="Delete"
 onClick={() => handleDelete(rec._id)}
 >
 <i className="fas fa-trash" />
 </Button>
 </PermissionWrapper>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>

 {!loading && inspections.length > 0 && (
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={inspections.length}
 limit={pagination.limit}
 onPageChange={(page) =>
 setPagination((prev) => ({ ...prev, currentPage: page }))
 }
 />
 )}
 </Tab.Pane>

 {/* TAB 2: NCR Monitor */}
 <Tab.Pane eventKey="ncr">
 <div className="p-3">
 <NCRMonitor />
 </div>
 </Tab.Pane>
 </Tab.Content>
 </Card>
 </Tab.Container>

 {/* View Modal */}
 {selectedRecord && (
 <QCViewModal
 show={showViewModal}
 record={selectedRecord}
 onHide={() => {
 setShowViewModal(false);
 setSelectedRecord(null);
 }}
 onRefresh={() => {
 fetchInspections();
 fetchStats();
 }}
 user={user}
 />
 )}
 </Fragment>
 );
};

export default QCList;
